import { useEffect } from 'react'
import { Controller, useForm } from 'react-hook-form'
import { t, Trans } from '@lingui/macro'
import { useLingui } from '@lingui/react'
import { Button, Dialog, DialogActions, DialogContent, DialogTitle, MenuItem, Stack, TextField } from '@mui/material'

import { ConfidenceLevel, IdentityJustificationTypeItem } from '@/core/models/identity-justification.model'

interface IdentityJustificationFormDialogProps {
  open: boolean
  initialValues: IdentityJustificationTypeItem
  onClose: () => void
  onSave: (item: IdentityJustificationTypeItem) => void
}

export function IdentityJustificationFormDialog({ open, initialValues, onClose, onSave }: IdentityJustificationFormDialogProps) {
  useLingui()

  const {
    control,
    register,
    handleSubmit,
    reset,
    formState: { errors },
  } = useForm<IdentityJustificationTypeItem>({
    defaultValues: initialValues,
  })

  useEffect(() => {
    if (open) {
      reset(initialValues)
    }
  }, [open, initialValues, reset])

  const onSubmit = (data: IdentityJustificationTypeItem) => {
    onSave(data)
  }

  return (
    <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth>
      <form onSubmit={handleSubmit(onSubmit)}>
        <DialogTitle>
          <Trans>Edit identity justification</Trans>
        </DialogTitle>
        <DialogContent>
          <Stack spacing={3} sx={{ mt: 1 }}>
            <TextField
              {...register('label', { required: t`Label is required`, maxLength: 255 })}
              label={t`Label`}
              helperText={errors.label?.message}
              error={!!errors.label}
              inputProps={{ maxLength: 64 }}
              fullWidth
            />
            <Controller
              control={control}
              name="confidenceLevel"
              rules={{ required: true }}
              render={({ field }) => (
                <TextField {...field} select label={t`Confidence level`} helperText={t`Only "High" confidence level allows identity validation.`} fullWidth>
                  <MenuItem value={ConfidenceLevel.LOW}>
                    <Trans>Low</Trans>
                  </MenuItem>
                  <MenuItem value={ConfidenceLevel.HIGH}>
                    <Trans>High</Trans>
                  </MenuItem>
                </TextField>
              )}
            />
          </Stack>
        </DialogContent>
        <DialogActions>
          <Button onClick={onClose}>
            <Trans>Cancel</Trans>
          </Button>
          <Button type="submit" variant="contained">
            <Trans>Save</Trans>
          </Button>
        </DialogActions>
      </form>
    </Dialog>
  )
}
